import { fromDisplay } from './currency';
import { C } from '../constants/theme';
import { soft, statusColors } from '@parsina/framework/tokens';

// ─────────────────────────────────────────────────────────────────────────────
// Settling an invoice — the vocabulary and the arithmetic, on neither surface in
// particular.
//
// It was `screens/Cashier/Cashier.shared.js` until «صندوق» was removed as a page
// (2026-09-12). The settle flow outlived the cash desk — پذیرش opens the drawer
// in place — so the derivations moved here, to `lib/`, where both the desktop
// `components/billing/SettleDrawer.jsx` and the phone's
// `mobile/sheets/MobileSettleSheet.jsx` read them. The cash-box parts went with
// the page: the shift reconciliation, the status tabs, the list's paging and its
// URL contract all described a screen that no longer exists.
//
// It exists because the desk and the phone answer the same money questions —
// what does this invoice owe, is it settled, may this line be removed, how much
// may still be charged — and a screen that recomputes any of those is wrong even
// on the day it happens to agree. The desktop drawer and the day panel already
// disagreed once about an invoice's balance (see lib/invoice.js); that is the
// class of defect this module closes for the settle flow.
//
// MONEY. Everything here is MINOR UNITS of the invoice's currency, like
// everything on the wire — WHOLE RIALS for IRR. The display unit (toman for IRR)
// exists only in a form field and at the display edge (`formatMoney`). A rial
// figure under a toman label reads as ten times the money. The helpers that read
// a form take the invoice's `currency` (blank is IRR).
// ─────────────────────────────────────────────────────────────────────────────

// ── Payment methods ──────────────────────────────────────────────────────────

/**
 * The five methods, with the rules the accounting service enforces.
 *
 * `enabled` must match the service's ENABLED_PAYMENT_METHODS — it refuses
 * anything else. `needsRef` names the methods the service refuses without the
 * terminal slip / transfer receipt / payer reference the cashier reconciles
 * against, so `refLabel` is the sentence the operator is shown when it is
 * missing, not decoration.
 */
export const PAYMENT_METHODS = (t) => ({
  CASH: {
    label: t('accounting.method.cash'),
    short: t('accounting.method.cash'),
    desc: t('cashier.methods.cash'),
    color: C.green, bg: C.greenLight, enabled: true,
  },
  CARD: {
    label: t('cashier.methods.pos'),
    short: t('accounting.method.card'),
    desc: t('cashier.methods.posDesc'),
    color: C.navy, bg: soft(C.navy, 0.1), enabled: true,
    needsRef: true, refLabel: t('cashier.methods.posRef'),
  },
  BANK_TRANSFER: {
    label: t('cashier.methods.bankTransfer'),
    short: t('cashier.methods.bankTransferShort'),
    desc: t('cashier.methods.bankTransferDesc'),
    color: C.teal, bg: C.tealLight, enabled: true,
    needsRef: true, refLabel: t('cashier.methods.bankTransferRef'),
  },
  THIRD_PARTY: {
    label: t('cashier.methods.thirdParty'),
    short: t('cashier.methods.thirdPartyShort'),
    desc: t('cashier.methods.thirdPartyDesc'),
    color: C.amber, bg: C.amberLight, enabled: true,
    needsRef: true, refLabel: t('cashier.methods.thirdPartyRef'),
  },
  ONLINE: {
    label: t('cashier.methods.patientApp'),
    short: t('reception.filters.online'),
    desc: t('common.state.comingSoon'),
    color: C.teal, bg: C.tealLight, enabled: false,
  },
});

/** The default method, so neither surface picks its own. */
export const DEFAULT_PAYMENT_METHOD = 'CASH';

// ── Status and item vocabulary ───────────────────────────────────────────────

export const STATUS_META = (t) => ({
  OPEN: { label: t('accounting.status.unpaid'), bg: '#FEF3C7', text: '#92400E' },
  INVOICE_PARTIALLY_PAID: { label: t('accounting.status.partiallyPaid'), bg: statusColors.inprogress.bg, text: statusColors.inprogress.text },
  PAID: { label: t('accounting.status.settled'), bg: '#D1FAE5', text: '#065F46' },
  VOID: { label: t('accounting.status.voidInvoice'), bg: '#FEE2E2', text: '#991B1B' },
  DRAFT: { label: t('accounting.status.draft'), bg: C.border, text: C.textMuted },
});

export const ITEM_TYPE_META = (t) => ({
  VISIT: { label: t('common.entity.visit'), icon: '🩺' },
  MEDICAL_SERVICE: { label: t('cashier.itemTypes.paraclinical'), icon: '🔬' },
  PRODUCT: { label: t('cashier.itemTypes.goods'), icon: '📦' },
  // «هزینهٔ لغو/عدم حضور» (PM-0657 C5): the no-show fee's own FINAL invoice line.
  FEE: { label: t('cashier.itemTypes.fee'), icon: '⏱' },
});

// ── Discounts ────────────────────────────────────────────────────────────────

/**
 * Mirrors the accounting service's `domain/discounts.resolve` — whole rials, and
 * NOT capped at the base.
 *
 * The split is the service's: on a LINE an over-large fixed discount is a typo the
 * editor rejects, while on an INVOICE it is a discount that outlived the lines it
 * was sized against, and the caller clamps it to the subtotal.
 *
 * Resolved locally rather than read off the stored figure, because editing a line
 * moves the subtotal a percentage discount is a percentage OF, and the stored one
 * is stale until the next fetch.
 */
export function resolveDiscount(type, value, base) {
  if (!Number.isFinite(value) || !Number.isFinite(base) || value <= 0 || base <= 0) return 0;
  const raw = type === 'PERCENT' ? (base * value) / 100 : value;
  return Math.round(raw);
}

/**
 * A percent reads as «۱۰٪ (۵۰٬۰۰۰ ت)»: the intent first, then what it came to.
 * `discountValue` is a PERCENT here, not money — formatNumber, never formatMoney,
 * or a 10% discount would render as «۱٪».
 */
export function discountLabel({ discountType, discountValue, discount }, t, formatNumber, formatMoney) {
  return discountType === 'PERCENT'
    ? t('cashier.discount.percentWithAmount', {
      percent: formatNumber(discountValue),
      amount: formatMoney(discount),
    })
    : t('cashier.discount.amount', { amount: formatMoney(discount) });
}

// ── Invoice arithmetic ───────────────────────────────────────────────────────

/**
 * A local line change re-derives the invoice's line-level roll-ups, since the
 * summary reads them off the invoice object.
 *
 * The invoice-WIDE figures (the resolved invoice discount and the payable under
 * it) are deliberately NOT set here: they depend on the discount resolved against
 * the new subtotal, which `settleState` recomputes.
 */
export function withLineItems(invoice, lineItems) {
  const sum = (field) => lineItems.reduce((s, li) => s + (li[field] || 0), 0);
  return {
    ...invoice,
    lineItems,
    totalAmount: sum('totalAmount'),
    insuranceShare: sum('insuranceShare'),
  };
}

/**
 * Everything the settle surface needs to know about one invoice, derived ONCE.
 *
 * This is the single behavioural truth the two surfaces share (§2.5): the same
 * fixture must produce the same answer on the desk and on the phone. Nothing that
 * renders an invoice may recompute any of these.
 *
 * @param {object} invoice  the invoice, with its line items — RIALS
 * @param {number} paid     Σ of the payments recorded against it — RIALS
 * @param {boolean} paidLoaded  whether that sum has landed yet
 * @param {number} heldDeposit  the مراجعه's HELD «پیش‌پرداخت» — RIALS (PM-0657).
 *   On an UNISSUED invoice it is set against what may still be taken (C7: the
 *   server refuses the rest with DEPOSIT_COVERS_DUE, and applies the deposit
 *   itself at issue). An issued invoice is never netted.
 */
export function settleState(invoice, paid, paidLoaded, heldDeposit = 0) {
  const lineItems = invoice?.lineItems || [];

  const lineDiscount = lineItems.reduce((s, li) => s + (li.discount || 0), 0);
  // Built exactly the way the service's `_apply_settlement` builds it: sum the
  // lines' patient share (already net of each line's own discount and the
  // insurer's cut), then take the invoice-wide discount off THAT.
  const subtotal = lineItems.reduce((s, li) => s + (li.patientPayable || 0), 0);
  // Clamped like `_apply_settlement`: a fixed discount can outlive the lines it
  // was sized against, and the patient's bill floors at zero rather than going
  // negative.
  const invDiscount = Math.min(
    resolveDiscount(invoice?.discountType, invoice?.discountValue, subtotal),
    subtotal,
  );
  const payable = Math.max(0, subtotal - invDiscount);
  const outstanding = Math.max(0, payable - paid);

  const isVoid = invoice?.status === 'VOID';
  const isSettled = lineItems.length > 0 && outstanding <= 0;
  // An ISSUED invoice is a confirmed obligation: its lines are never edited or
  // removed. A correction is a negative line, or void-and-reissue.
  const frozen = !!invoice?.isIssued;
  const canRemove = paid <= 0 && !isSettled && !frozen;
  // PM-0699 — the DISCOUNT belongs to the مراجعه and stays editable until the
  // مراجعه is SETTLED, even once the invoice is issued: ending the visit issues it,
  // and the doctor's page has no discount control, so freezing it with the lines left
  // the desk no way to give one. The lines stay frozen. An unissued invoice keeps its
  // old rule (editable at any time, even settled). For an ISSUED one the paid total
  // must have landed first — before it, `outstanding` reads the whole payable and
  // would offer an edit the service is about to refuse (DISCOUNT_ON_SETTLED).
  const canEditDiscount = !isVoid && (!frozen || (paidLoaded && !isSettled));

  // Editing a line down can drop the payable below what the patient already paid.
  // Accounting has no refund or credit-note concept to book the surplus against,
  // so it would otherwise disappear behind a tidy zero balance. Name it instead.
  const overpaid = Math.max(0, paid - payable);

  // Mirrors the service's `_apply_settlement` (status = payments vs. payable), so
  // it stays right as lines are added, edited or removed. Before the paid total
  // lands `paid` is 0 and the derivation would wrongly read OPEN, so trust the
  // server's status for that first beat — and always for a frozen voided invoice.
  const shownStatus = (!paidLoaded || isVoid)
    ? invoice?.status
    : paid <= 0 ? 'OPEN'
      : paid + 0.5 >= payable ? 'PAID'
        : 'INVOICE_PARTIALLY_PAID';

  // An invoice with no line items (created straight from a reception appointment)
  // has no priced fee yet: the cashier types the appointment's price and it is
  // added as the VISIT line at pay time.
  const isEmptyInvoice = lineItems.length === 0 && paid <= 0;
  // The held deposit counts only against a priced, unissued invoice: an issued
  // one already has it in `paid`, and an empty one has no price to net it from.
  const held = !frozen && !isEmptyInvoice ? Math.max(0, Number(heldDeposit) || 0) : 0;
  // What the desk may still take NOW — the balance less the held deposit.
  const dueNow = Math.max(0, outstanding - held);
  // Cap the amount at the outstanding balance only when there IS a balance; for an
  // empty invoice any positive amount is the appointment fee.
  const maxChargeable = isEmptyInvoice ? Infinity : dueNow;

  return {
    lineDiscount, subtotal, invDiscount, payable, outstanding,
    isVoid, isSettled, frozen, canRemove, canEditDiscount, overpaid,
    shownStatus, isEmptyInvoice, maxChargeable,
    heldDeposit: held, dueNow,
  };
}

/** A line the cashier may still take off the invoice. The VISIT line never is, nor a fee. */
export const canRemoveLine = (item, canRemove) => canRemove && item?.itemType !== 'VISIT' && item?.itemType !== 'FEE';

/**
 * A line the desk does not edit: the encounter's own service (re-pointed through
 * «تغییر خدمت», never here) and a no-show fee (PM-0657 — a fact of the policy,
 * reversed by voiding its invoice).
 */
export const isLockedLine = (item) => item?.role === 'PRIMARY' || item?.role === 'FEE' || item?.itemType === 'FEE';

/**
 * The `handlePay` guards, in the order the desk applies them, as one answer.
 *
 * Returns the message to show, or '' when the payment may proceed. Order matters:
 * a missing reference is reported before an invalid amount, because the operator
 * fixes the field they are looking at.
 */
export function validatePayment({ t, method, reference, amountRial, maxChargeable }) {
  const m = PAYMENT_METHODS(t)[method];
  if (!m || !m.enabled) return t('validation.payment.methodRequired');
  if (m.needsRef && !String(reference ?? '').trim()) {
    return t('validation.payment.referenceRequired', { label: m.refLabel });
  }
  if (!(amountRial > 0)) return t('validation.payment.amountInvalid');
  if (amountRial > maxChargeable + 0.5) return t('validation.payment.exceedsRemaining');
  return '';
}

/**
 * Whether the commit control is live. Deliberately NOT `!validatePayment(...)`:
 * the button must not be greyed for a missing reference — the operator has to be
 * able to press it and be told which field is empty (§2.3, «title» is not an
 * affordance).
 */
export function canSubmitPayment({ t, method, amountRial, maxChargeable }) {
  const m = PAYMENT_METHODS(t)[method];
  return Boolean(m?.enabled) && amountRial > 0 && amountRial <= maxChargeable + 0.5;
}

/** نوبت‌دهی → ویزیت → صدور → تسویه, and how far this invoice has come. */
export function journeySteps(t, { hasLines, frozen, isSettled }) {
  return [
    { id: 'booked', icon: '📅', label: t('cashier.itemTypes.appointment'), done: true },
    { id: 'visit', icon: '🩺', label: t('common.entity.visit'), done: hasLines },
    { id: 'issued', icon: '🧾', label: t('cashier.invoice.issue'), done: frozen },
    { id: 'settled', icon: '💳', label: t('accounting.actions.settle'), done: isSettled },
  ];
}

/** «پیش‌فاکتور» or «فاکتور #{n}» — the badge that says whether the lines are still movable. */
export function issuanceBadge(t, invoice) {
  const frozen = !!invoice?.isIssued;
  if (!frozen) return { frozen, label: t('cashier.invoice.proforma'), hint: t('cashier.invoice.proformaHint') };
  return {
    frozen,
    label: invoice?.invoiceNumber
      ? t('cashier.invoice.invoiceNumbered', { number: invoice.invoiceNumber })
      : t('cashier.invoice.invoiceFinal'),
    hint: t('cashier.invoice.frozenHint'),
  };
}

// ── Editing a line, and the rules the service will apply to it ───────────────

/** '' → NaN rather than 0, so a cleared field reads as invalid instead of as zero. */
const num = (v) => (v === '' || v == null ? NaN : Number(v));

/**
 * What the service will compute from the form the cashier is looking at.
 *
 * The form holds what a human TYPES — toman, or the invoice currency's display
 * unit — as raw strings; the minor-unit figures are derived from it, and only
 * those are ever sent. Holding minor units in the form and converting per
 * keystroke fights the user's cursor on every separator.
 *
 * A PERCENT `discountValue` is a percent, not money, and does not convert.
 */
export function lineEditPreview(form, currency) {
  const isPct = form.discountType === 'PERCENT';
  const qty = num(form.quantity);
  const unitT = num(form.unitTariff);        // toman, as typed
  const dval = num(form.discountValue);      // percent when isPct, else toman
  const insT = num(form.insuranceShare);     // toman, as typed
  const unit = fromDisplay(form.unitTariff, currency);
  const ins = fromDisplay(form.insuranceShare, currency);
  const total = qty * unit;
  // A line discount is a percentage of that line's own gross — the base the
  // service uses.
  const disc = isPct ? resolveDiscount('PERCENT', dval, total) : fromDisplay(form.discountValue, currency);
  const payable = Math.max(0, total - disc - ins);
  return { isPct, qty, unitT, dval, insT, unit, ins, total, disc, payable };
}

/**
 * The accounting service's own line rules, checked against the TOMAN the cashier
 * typed so «نمی‌تواند منفی باشد» points at something they can see. Returns '' when
 * the edit may be sent.
 */
export function validateLineEdit(t, form, currency) {
  const { isPct, qty, unitT, dval, insT, total, disc, ins } = lineEditPreview(form, currency);
  if (!String(form.description ?? '').trim()) return t('validation.lineItem.descriptionRequired');
  if (!Number.isInteger(qty) || qty <= 0) return t('validation.lineItem.quantityPositiveInteger');
  if (!Number.isFinite(unitT) || unitT < 0) return t('validation.lineItem.unitPriceNotNegative');
  if (!Number.isFinite(dval) || dval < 0) return t('validation.lineItem.discountNotNegative');
  if (isPct && dval > 100) return t('validation.lineItem.discountPercentMax');
  if (!Number.isFinite(insT) || insT < 0) return t('validation.lineItem.insuranceNotNegative');
  if (disc + ins > total + 0.5) return t('validation.lineItem.discountPlusInsuranceTooHigh');
  return '';
}

/**
 * Replacing a COMPUTED insurer share with a human's number: its own act, its own
 * permission (`payments.manage`), and a MANDATORY reason — the service keeps what
 * the rules said beside what the person decided, and a blank reason makes that
 * record worthless.
 *
 * @param gross the line's amount after its own discount, in RIALS
 */
export function validateInsuranceOverride(t, { amountToman, reason, gross, currency }) {
  const rial = fromDisplay(amountToman, currency);
  if (!Number.isFinite(rial) || rial < 0) return t('validation.lineItem.insuranceNotNegative');
  if (rial > gross + 0.5) return t('validation.insurance.overrideRange');
  if (!String(reason ?? '').trim()) return t('validation.insurance.overrideReason');
  return '';
}

/** The invoice-wide discount's rules. Its value is a percent, or toman. */
export function validateInvoiceDiscount(t, { type, value }) {
  const v = num(value);
  if (!Number.isFinite(v) || v < 0) return t('validation.lineItem.discountNotNegative');
  if (type === 'PERCENT' && v > 100) return t('validation.lineItem.discountPercentMax');
  return '';
}

// ── Small shared formatters ──────────────────────────────────────────────────

export function fmtDate(ts, formatDateTime) {
  if (!ts) return '—';
  const n = typeof ts === 'string' ? Number(ts) : ts;
  const d = n > 1e10 ? new Date(n) : new Date(n * 1000);
  return formatDateTime(d, 'short');
}

export function patientInitials(name, t) {
  if (!name) return t('common.placeholder.unknownInitial');
  return name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('');
}

// ── Reversals: what a payment IS, and what may be done to it ─────────────
//
// Promoted here from `screens/Accounting/views/Receipts.shared.js` when the
// DESK gained a refund of its own (docs/CLINIC_RECEPTION_FINANCIAL_EVENTS_DESIGN.md
// §8.3). Two surfaces now ask «may this payment be given back?» — the receipts
// ledger and the settle drawer — and two copies of that predicate would agree
// on the day they were written and drift the week after, which at a cash desk
// means one screen offering a refund the other refuses.
//
// Receipts.shared.js re-exports these, so every existing consumer is unchanged.

// A payment is never deleted, only reversed. A voided row stays on the ledger —
// struck through, counted by nothing — because money that arrived and went back
// is two facts, and a page that could only show the second one would be lying
// about the first.
export const isVoid = (p) => p?.status === 'VOID';

// A REFUND is the other way money goes back: not "this should never have been
// taken" but "the patient is owed some of it" — a new signed row into TODAY's
// drawer, so the original payment is untouched and both facts survive.
export const isRefundRow = (p) => p?.kind === 'REFUND';

/** What is still returnable against a payment, in RIALS. */
export const refundableOf = (p) => (p?.amount || 0) - (p?.refundedAmount || 0);

// ── What may be DONE to a row ────────────────────────────────────────────────

/**
 * A payment may only be reversed while the drawer it was banked into is still
 * open: a closed shift has had its cash counted and handed over.
 *
 * `shiftStatus == null` means it was banked into no drawer at all, which is NOT
 * "closed" — there is no reconciled count to protect, so such a payment stays
 * correctable. The server enforces all of this; these predicates only decide
 * whether to OFFER the control, because a button that always fails is worse
 * than no button.
 */
export const canVoid = (p) => !isVoid(p) && p?.shiftStatus !== 'CLOSED';

/**
 * Only a live, real payment with something left to return. A credit-settled one
 * goes back through «علی‌الحساب» instead, and a pending/failed/cancelled
 * transaction never became money in the first place.
 *
 * The 0.5-rial floor is not a rounding fudge: it stops a payment refunded to
 * the last rial offering a refund of nothing because a float subtraction left
 * 0.0000001 behind.
 */
export const canRefund = (p) => !isVoid(p) && !isRefundRow(p)
  && p?.status !== 'PENDING' && p?.status !== 'FAILED' && p?.status !== 'CANCELLED'
  && p?.paymentMethod !== 'CREDIT'
  && refundableOf(p) > 0.5;

/** The methods money may be handed BACK by. Never `CREDIT`, never `ONLINE`. */
export const REFUND_METHODS = ['CASH', 'CARD', 'BANK_TRANSFER', 'THIRD_PARTY'];

/**
 * The methods the accounting service demands a reference for. A POS slip, a
 * transfer and a third-party settlement each leave a number somewhere outside
 * Parsina, and a refund nobody can trace back to it is not reconcilable.
 */
export const refundNeedsReference = (method) =>
  method === 'CARD' || method === 'BANK_TRANSFER' || method === 'THIRD_PARTY';


